import { AccessTime, ExitToApp, SwapCalls } from '@mui/icons-material';
import { FormControlLabel, Radio, RadioGroup, Tooltip } from '@mui/material';
import { useState } from 'react';
import { CustomCampaignSteps } from 'domains/campaigns/components/CustomCampaign/CustomCampaign';
import { addBranchSend } from 'domains/campaigns/components/CustomCampaign/customCampaignActions';
import { CampaignStateTypes } from 'domains/campaigns/components/CustomCampaign/customCampaignReducer';
import { SplitTypes } from 'domains/campaigns/types';
import FormLayout from 'domains/core/components/FormLayout';
import { MAX_SENDS_AMOUNT } from '../utils';
import {
    disabledButtonStyles,
    iconStyles,
    labelStyles,
    sharedButtonStyles,
} from '../CustomCampaignChooseNextStep/CustomCampaignChooseNextStep.styles';

const EXIT_FLOW_ACTION = 'exit-flow';

type Props = {
    activeSendIndex: number;
    campaignState: CampaignStateTypes;
    dispatchCampaignAction: React.Dispatch<any>;
    setActiveSendIndex: (index: number) => void;
    setActiveStep: (step: CustomCampaignSteps) => void;
};

const findDoesNotMeetSendIndex = (sends: CampaignStateTypes['sends'], parentIndex: number): number =>
    sends.findIndex(
        (send: any) => send.splitType === SplitTypes.DOES_NOT_MEET && (send.parentIndex ?? 0) === parentIndex
    );

/**
 * Intermediate "choose next step" screen shown after completing an email
 * within a conditional split branch (meets / does not meet criteria).
 *
 * Options:
 *  - Time delay: add another email to the current branch (chained via parentIndex)
 *  - Exit flow:  proceed to the other branch or finish the conditional split
 */
const CustomCampaignChooseNextStepConditionalSplit = ({
    activeSendIndex,
    campaignState,
    dispatchCampaignAction,
    setActiveSendIndex,
    setActiveStep,
}: Props) => {
    const [selectedStep, setSelectedStep] = useState<string | null>(null);

    const activeSend = campaignState.sends[activeSendIndex] as any;
    const maxSendsReached = campaignState.sends.length >= MAX_SENDS_AMOUNT;

    // Walk up the parent chain to find the effective splitType and branch root parentIndex
    const getEffectiveSplitType = (send: any): string | undefined => {
        if (send?.splitType) return send.splitType;
        if (send?.parentIndex != null) {
            return getEffectiveSplitType(campaignState.sends[send.parentIndex] as any);
        }
        return undefined;
    };

    const getBranchRootParentIndex = (send: any): number => {
        if (send?.splitType) {
            if (send?.parentIndex != null) {
                const parent = campaignState.sends[send.parentIndex] as any;
                if (getEffectiveSplitType(parent) === send.splitType) {
                    return getBranchRootParentIndex(parent);
                }
            }
            return send.parentIndex ?? 0;
        }
        if (send?.parentIndex != null) {
            return getBranchRootParentIndex(campaignState.sends[send.parentIndex] as any);
        }
        return 0;
    };

    const effectiveSplitType = getEffectiveSplitType(activeSend);
    const isMeetsCriteriaBranch = effectiveSplitType === SplitTypes.MEETS;
    const branchRootParentIndex = getBranchRootParentIndex(activeSend);

    const handleStepClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        const step = (event.target as HTMLInputElement).value;
        setSelectedStep(step);

        if (step === CustomCampaignSteps.SET_TIME_DELAY) {
            const branchSplitType = isMeetsCriteriaBranch ? SplitTypes.MEETS : SplitTypes.DOES_NOT_MEET;

            dispatchCampaignAction(addBranchSend(activeSendIndex, branchSplitType));

            if (isMeetsCriteriaBranch) {
                const doesNotMeetIndex = findDoesNotMeetSendIndex(campaignState.sends, branchRootParentIndex);
                setActiveSendIndex(doesNotMeetIndex !== -1 ? doesNotMeetIndex : campaignState.sends.length);
            } else {
                setActiveSendIndex(campaignState.sends.length);
            }

            setActiveStep(CustomCampaignSteps.SET_TIME_DELAY);
        }

        if (step === EXIT_FLOW_ACTION) {
            if (isMeetsCriteriaBranch) {
                const doesNotMeetIndex = findDoesNotMeetSendIndex(campaignState.sends, branchRootParentIndex);
                if (doesNotMeetIndex !== -1) {
                    setActiveSendIndex(doesNotMeetIndex);
                    setActiveStep(CustomCampaignSteps.SELECT_PATH_ACTION);
                }
            } else {
                setActiveStep(CustomCampaignSteps.CHOOSE_NEXT_STEP);
            }
        }
    };

    const branchLabel = isMeetsCriteriaBranch ? 'meets' : 'does not meet';
    const exitLabel = isMeetsCriteriaBranch ? 'proceed to "does not meet criteria"' : 'done with conditional split';

    const guidingText = `You've completed an email for the "${branchLabel} the criteria" path. Choose to add another time-delayed email to this path, or proceed to the next step.`;

    const steps = [
        {
            disabled: maxSendsReached,
            disabledMessage: "You've reached the maximum number of sends.",
            icon: <AccessTime sx={iconStyles} />,
            label: 'time delay',
            value: CustomCampaignSteps.SET_TIME_DELAY,
        },
        {
            disabled: false,
            disabledMessage: '',
            icon: <ExitToApp sx={iconStyles} />,
            label: exitLabel,
            value: EXIT_FLOW_ACTION,
        },
    ];

    return (
        <FormLayout guidingText={guidingText} header="choose next step" stepName="actions" icon={<SwapCalls />}>
            <RadioGroup onChange={handleStepClick} value={selectedStep}>
                {steps.map((step) => (
                    <Tooltip key={step.value} title={step.disabled ? step.disabledMessage : ''}>
                        <FormControlLabel
                            disabled={step.disabled}
                            control={<Radio sx={{ display: 'none' }} />}
                            label={
                                <span style={labelStyles}>
                                    {step.icon}
                                    {step.label}
                                </span>
                            }
                            sx={{
                                ...sharedButtonStyles,
                                ...(step.disabled && disabledButtonStyles),
                            }}
                            value={step.value}
                        />
                    </Tooltip>
                ))}
            </RadioGroup>
        </FormLayout>
    );
};

export default CustomCampaignChooseNextStepConditionalSplit;
